import { useReactiveVar } from '@apollo/client';

import { userStateVar } from 'core/state/userState';
import { UserRole } from 'types';

import {
  COMMUNICATOR_ROLES,
  FINANCE_EXPENSE_ROLES,
  TASK_TRACKER_ROLES,
  TEACHER_CARD_ROLES,
  TEACHER_PROFILE_ROLES,
} from '../Palette/paletteRoles';
import { TEACHER_TERMS_ROLES } from '../TeacherBilling/teacherBillingModel';

/**
 * Capability booleans for the Teacher card, derived from the current user's
 * roles. Mirrors `useStudentPermissions`. Hiding a control is a UX affordance
 * only — the backend remains the authority on each mutation.
 */
export interface ITeacherPermissions {
  /** Edit the teacher record + append applications + profile fields. */
  canManageTeacher: boolean;
  /** Assign to a group via the «Заявка» flow. */
  canAddApplication: boolean;
  /** Edit the profile fields (rating / note / courses). */
  canEditProfile: boolean;
  /** Write an email to the teacher. */
  canContactEmail: boolean;
  /** Link / unlink PandaDoc contracts. */
  canManagePandadoc: boolean;
  /**
   * Read the survey-derived feedback report (spec 0027). A DIFFERENT gate from
   * `canManageTeacher`: the backend's TEACHER_INSIGHT_ROLES is the survey
   * managers + HR, so Methodist is in and SalesManager is out. The tab must be
   * hidden for the rest — a denied query REJECTS the Apollo promise and would
   * surface as a red toast (the ContractTab/PandaDoc lesson).
   */
  canReadSurveyFeedback: boolean;
  /**
   * Read the hiring dossier (spec 0028): interviews, AI competency profile,
   * candidate documents — and the forward-load panels, which sit behind the same
   * backend gate.
   *
   * The same role set as `canManageTeacher`, but a SEPARATE flag: it mirrors a
   * different backend constant (`TEACHER_CARD_ROLES` / `INTERVIEW_ROLES`), and
   * conflating them would silently drift the day either side changes.
   *
   * FinanceManager and Methodist reach `/teachers/:id/details`
   * (`TEACHER_PROFILE_ROLES`) but are NOT in that set, so without this flag the
   * load query fired on every card open and the «Найм» tab offered three
   * queries the backend denies — and a denied query REJECTS the Apollo promise
   * (the ContractTab/PandaDoc lesson).
   */
  canReadTeacherDossier: boolean;
  /**
   * Выплаты преподавателю — это расходы (`expenses`, `expenseAggregates` с
   * `teacherId`), а их бэкенд отдаёт только `EXPENSES_ACCESS_ROLES` (Admin,
   * FinanceManager; `FINANCE_EXPENSE_ROLES` приложения). Карточку открывают
   * шире (AM, продажи, HR), и без флага плитка «Выплаты» и вкладка читали
   * отказ как «0.00 PLN» — решение владельца 06.10.2026: прятать.
   */
  canReadPayouts: boolean;
  /**
   * Служебная часть досье: рейтинг и внутренняя заметка, доступ к LMS,
   * отзывы студентов (рейтинг и именные комментарии), тип договора, запись
   * собеседования. Зеркало `erp_teacher_card_viewer` бэкенда
   * (`TEACHER_CARD_ROLES`); методист досье открывает (решение владельца
   * 09.10), но эти поля приходят ему пустыми — без флага «Профиль» рисовал
   * «нет оценки» и «нет отзывов» там, где они есть, но ему не положены.
   */
  canReadPrivateProfile: boolean;
  /**
   * ТЗ 0114 — финансовые условия: реквизиты для фактур и ставки заявок
   * (`TEACHER_TERMS_ROLES` бэкенда = роли карточки + FinanceManager).
   * Финансист досье открывает в публичном виде (как методист), но ставки
   * заявок и контакты сервер ему отдаёт — «Группы» показывают ставку, а
   * «Профиль» — контакты и блок «Реквизиты для фактур».
   */
  canReadTerms: boolean;
  /**
   * «Общение» — переписка преподавателя (`teacherConversationsPage`):
   * `COMM_ACCESS_ROLES` бэкенда (= `MANAGEMENT_ROLES`, без методиста).
   */
  canReadCommunications: boolean;
  /**
   * «Задачи» — задачи о преподавателе (`staffTasks(teacherId)`):
   * `TASK_ACCESS_ROLES` бэкенда (= `MANAGEMENT_ROLES`, без методиста).
   */
  canReadTasks: boolean;
}

// Mirror the BACKEND role gates exactly — a mismatch shows a control the
// backend then denies (silent failure / misleading empty state).
// Teacher schema (query + mutations incl. setTeacherProfile / applications):
// backend `TEACHER_CARD_ROLES` — one list for the route, the menu and here.
const TEACHER_ROLES = TEACHER_CARD_ROLES;
// PandaDoc schema (pandadocDocuments + link/unlink) — NB: HRManager is NOT
// allowed here, but FinanceManager is.
const PANDADOC_ROLES = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.FINANCIER, // FinanceManager
];
// surveys.schema.TEACHER_INSIGHT_ROLES = SURVEY_MANAGER_ROLES + HRManager.
// NB: Methodist IS allowed and SalesManager is NOT — the exact inverse of
// PANDADOC_ROLES on both counts.
const SURVEY_INSIGHT_ROLES = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT, // AccountManager
  UserRole.METHODIST,
  UserRole.HRMANAGER,
];

const hasAnyRole = (roles: UserRole[], allowed: UserRole[]): boolean =>
  roles.some((role) => allowed.includes(role));

export const useTeacherPermissions = (): ITeacherPermissions => {
  const userState = useReactiveVar(userStateVar);
  const roles = (userState.me?.roles ?? []) as UserRole[];

  const canManageTeacher = hasAnyRole(roles, TEACHER_ROLES);

  return {
    canManageTeacher,
    canAddApplication: canManageTeacher,
    canEditProfile: canManageTeacher,
    canContactEmail: canManageTeacher,
    canManagePandadoc: hasAnyRole(roles, PANDADOC_ROLES),
    canReadSurveyFeedback: hasAnyRole(roles, SURVEY_INSIGHT_ROLES),
    // teachers_query.TEACHER_CARD_ROLES == teachers.schema INTERVIEW_ROLES ==
    // TEACHER_ROLES here. FinanceManager is deliberately absent from all three.
    canReadTeacherDossier: hasAnyRole(roles, TEACHER_ROLES),
    canReadPayouts: hasAnyRole(roles, FINANCE_EXPENSE_ROLES),
    canReadPrivateProfile: hasAnyRole(roles, TEACHER_CARD_ROLES),
    canReadTerms: hasAnyRole(roles, TEACHER_TERMS_ROLES),
    canReadCommunications: hasAnyRole(roles, COMMUNICATOR_ROLES),
    canReadTasks: hasAnyRole(roles, TASK_TRACKER_ROLES),
  };
};

/**
 * Можно ли роли ОТКРЫТЬ карточку преподавателя (`TEACHER_PROFILE_ROLES`:
 * роли карточки, методист — решение владельца 09.10 — и финансист, ТЗ 0114).
 * Остальным имя преподавателя — простым текстом, а не ссылкой в «Не найдено».
 */
export const useCanOpenTeacherCard = (): boolean => {
  const userState = useReactiveVar(userStateVar);
  const roles = (userState.me?.roles ?? []) as UserRole[];
  return hasAnyRole(roles, TEACHER_PROFILE_ROLES);
};
